import React, { useState } from 'react';
import { FaCopy, FaTimes, FaDownload, FaCode } from 'react-icons/fa';

const SQLQueryModal = ({ isOpen, onClose, query, metadata, loading, error }) => {
  const [copied, setCopied] = useState(false);
  const [showFormatted, setShowFormatted] = useState(true);

  if (!isOpen) return null;

  // Copy to clipboard function
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(query);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = query;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Download as SQL file
  const handleDownload = () => {
    if (!query || !metadata) return;

    const blob = new Blob([query], { type: 'text/sql' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `route-summary-${metadata.groupingName?.replace(/[^a-zA-Z0-9]/g, '-')}-${metadata.startDate}-to-${metadata.endDate}.sql`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <FaCode className="text-blue-500 text-xl" />
            <div>
              <h2 className="text-xl font-semibold text-gray-800">SQL Query Generator</h2>
              {metadata && (
                <p className="text-sm text-gray-600 mt-1">
                  {metadata.groupingName} • {formatDate(metadata.startDate)} to{' '}
                  {formatDate(metadata.endDate)}
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <FaTimes className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-gray-600">Generating SQL query...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-red-600">
                <p className="text-lg font-medium mb-2">Error generating SQL query</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          ) : query ? (
            <>
              {/* Metadata Info */}
              {metadata && (
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Date Range:</span>
                      <span className="ml-2 text-gray-600">{metadata.dateCount} days</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Min Weight:</span>
                      <span className="ml-2 text-gray-600">{metadata.minWeight} tonnes</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Dispatch Locations:</span>
                      <span className="ml-2 text-gray-600">
                        {metadata.dispatchLocations?.length || 0}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Receipt Locations:</span>
                      <span className="ml-2 text-gray-600">
                        {metadata.receiptLocations?.length || 0}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Controls */}
              <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setShowFormatted(!showFormatted)}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      showFormatted
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {showFormatted ? 'Formatted' : 'Raw'}
                  </button>
                  <span className="text-sm text-gray-500">
                    {query.split('\n').length} lines • {query.length} characters
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleCopy}
                    className={`flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                      copied
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <FaCopy className="mr-2" />
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={handleDownload}
                    className="flex items-center px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                  >
                    <FaDownload className="mr-2" />
                    Download
                  </button>
                </div>
              </div>

              {/* SQL Query Display */}
              <div className="flex-1 overflow-auto">
                <pre
                  className={`p-6 text-sm font-mono leading-relaxed ${
                    showFormatted
                      ? 'whitespace-pre-wrap text-gray-800'
                      : 'whitespace-pre text-gray-700'
                  }`}
                >
                  <code className="language-sql">{query}</code>
                </pre>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-gray-500">No query available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <p>
                This query is written for PostgreSQL and can be run in pgAdmin, DBeaver, psql or any
                PostgreSQL client.
              </p>
              <p className="mt-1">
                <strong>Note:</strong> Ensure you have read access to the{' '}
                <code>lexi_bridge_data_view</code> view.
              </p>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SQLQueryModal;
